import { type UUID } from '~/types/common';

import { type CompaniesAdminQueryParams } from './useQueryCompaniesAdmin';
import { type CompanyAliasAddressesSearchParameters } from './useQueryCompanyAliasAddresses';
import { type CompanyDuplicateCheckPayload } from './useQueryCompanyDuplicateCheck';
import { type CompanyFromAliasAddressSearchParameters } from './useQueryCompanyFromAliasAddress';

export const queryKeysCompany = {
  base: () => 'company' as const,
  getActivationRoutingDiagnosis: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'activationRoutingDiagnosis',
      { companyId },
    ] as const,
  getAdmin: (companyId: UUID) =>
    [queryKeysCompany.base(), 'detail', 'admin', { companyId }] as const,
  getAiSettings: (companyId: UUID) =>
    [queryKeysCompany.base(), 'detail', 'aiSettings', { companyId }] as const,
  getAliasAddress: (aliasAddressId: UUID) =>
    [
      queryKeysCompany.base(),
      'aliasAddress',
      'detail',
      { aliasAddressId },
    ] as const,
  getAliasAddresses: (searchParams: CompanyAliasAddressesSearchParameters) =>
    [queryKeysCompany.base(), 'aliasAddress', 'list', searchParams] as const,
  getAliasAddressesForCompany: (companyId: UUID) =>
    [queryKeysCompany.base(), 'aliasAddress', 'list', { companyId }] as const,
  getAllAdmin: (queryParams: Partial<CompaniesAdminQueryParams>) =>
    [queryKeysCompany.base(), 'list', 'admin', queryParams] as const,
  getApiKeys: (companyId: UUID) =>
    [queryKeysCompany.base(), 'detail', 'apiKeys', { companyId }] as const,
  getByIdsAdmin: (companyIds: readonly UUID[]) =>
    [...queryKeysCompany.getByIdsAdminBase(), { companyIds }] as const,
  getByIdsAdminBase: () => [queryKeysCompany.base(), 'list', 'byIds'] as const,
  getCompanyFromAddress: (
    searchParams: CompanyFromAliasAddressSearchParameters,
  ) => [queryKeysCompany.base(), 'fromAliasAddress', searchParams] as const,
  getDeletionCheck: ({ companyId }: { companyId: UUID }) =>
    [queryKeysCompany.base(), 'deletionCheck', { companyId }] as const,
  getDuplicateCheck: (payload: CompanyDuplicateCheckPayload | undefined) =>
    [queryKeysCompany.base(), 'duplicateCheck', { payload }] as const,
  getInvoiceAllowedSenderAddresses: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'invoiceAllowedSenderAddresses',
      { companyId },
    ] as const,
  getInvoiceCheckDelay: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'invoiceCheckDelay',
      { companyId },
    ] as const,
  getInvoiceMailbox: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'invoiceMailbox',
      { companyId },
    ] as const,
  getInvoiceReceivers: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'invoiceReceivers',
      { companyId },
    ] as const,
  getInvoiceSendingSettings: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'invoiceSendingSettings',
      { companyId },
    ] as const,
  getLogo: (companyId: UUID) =>
    [queryKeysCompany.base(), 'detail', 'logo', { companyId }] as const,
  getMoveCheck: ({
    companyId,
    targetCompanyAccountId,
  }: {
    companyId: UUID;
    targetCompanyAccountId?: UUID | undefined;
  }) =>
    [
      queryKeysCompany.base(),
      'moveCheck',
      { companyId, targetCompanyAccountId },
    ] as const,
  getSendingCompanies: () =>
    [queryKeysCompany.base(), 'sendingCompanies'] as const,
  getSettings: (companyId: UUID) =>
    [queryKeysCompany.base(), 'detail', 'settings', { companyId }] as const,
  getSupplierAlertConfigs: () =>
    [queryKeysCompany.base(), 'supplierAlertConfig'] as const,
  getSupplierLetter: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'supplierLetter',
      { companyId },
    ] as const,
  getSupplierLetters: () =>
    [queryKeysCompany.base(), 'detail', 'supplierLetter'] as const,
  getWatermarkSettings: (companyId: UUID) =>
    [
      queryKeysCompany.base(),
      'detail',
      'watermarkSettings',
      { companyId },
    ] as const,
} as const;
